import { FormattedMessage } from 'react-intl';
import { formatDate } from 'date-fns';

import { useMemberId } from '@trello/authentication';
import { useIsOrganizationMembershipType } from '@trello/business-logic-react/organization';
import { forTemplate } from '@trello/legacy-i18n';
import { CollaboratorAvatar } from '@trello/member-avatar';
import { dangerouslyConvertPrivacyString } from '@trello/privacy';
import type { TeamTestIds } from '@trello/test-ids';
import { getTestId } from '@trello/test-ids';

import { AddToWorkspaceButton } from 'app/src/components/AddGuestToWorkspace';
import { RemoveMemberFromWorkspaceButton } from './RemoveMemberFromWorkspaceButton';
import { WorkspaceBoards } from './WorkspaceBoards';
import { useWorkspaceCollaboratorItemFragment } from './WorkspaceCollaboratorItemFragment.generated';
import { useWorkspaceMembersListOrganizationFragment } from './WorkspaceMembersListOrganizationFragment.generated';

import styles from './WorkspaceCollaboratorItem.less';

// intl.formatMessage doesn't appear to support shouldEscapeStrings: false
const format = forTemplate('select_member', {
  shouldEscapeStrings: false,
});

interface WorkspaceCollaboratorItemProps {
  idMember: string;
  idOrganization: string;
}

export function WorkspaceCollaboratorItem({
  idMember,
  idOrganization,
}: WorkspaceCollaboratorItemProps) {
  const currentMemberId = useMemberId();

  const { data: member } = useWorkspaceCollaboratorItemFragment({
    from: { id: idMember },
    optimistic: true,
  });

  const { data: org } = useWorkspaceMembersListOrganizationFragment({
    from: { id: idOrganization },
    optimistic: true,
  });

  const isCurrentMemberWorkspaceAdmin = useIsOrganizationMembershipType({
    idMember: currentMemberId,
    idOrganization,
    expectedMemberType: 'admin',
  });

  const lastActiveDate =
    member?.dateLastImpression &&
    formatDate(new Date(member?.dateLastImpression), 'MMMM yyyy');

  return (
    <div
      className={styles.workspaceMemberListItem}
      data-testid={getTestId<TeamTestIds>('workspace-member-item')}
    >
      <CollaboratorAvatar
        idMember={idMember}
        size={32}
        deactivated={member?.activityBlocked}
      />
      <div className={styles.memberListItemNames}>
        <p>
          <strong>{member?.fullName}</strong>
        </p>
        <p className={styles.memberListItemUsernameAndLastActive}>
          <span>
            {member?.username &&
              `@${dangerouslyConvertPrivacyString(member.username)}`}
          </span>
          &nbsp;
          {lastActiveDate && (
            <span>
              <span className={styles.bulletSeparator}>&#x2022;</span>
              <FormattedMessage
                id="templates.member_list_item_detail.last-active-in-trello"
                defaultMessage="Last active in Trello {lastActiveDate}"
                description="Date the Workspace guest was last active in Trello."
                values={{ lastActiveDate }}
              />
            </span>
          )}
          {!lastActiveDate && (
            <span>
              <span className={styles.bulletSeparator}>&#x2022;</span>
              <FormattedMessage
                id="templates.member_list_item_detail.no-recent-activity"
                defaultMessage="No recent activity"
                description="User has not been active in Trello recently"
              />
            </span>
          )}
        </p>
        {org?.enterprise?.displayName && (
          <p>
            {org?.idEnterprise === member?.idEnterprise
              ? format('managed-by-enterprise', {
                  enterpriseName: org?.enterprise?.displayName,
                })
              : format('not-managed-by-enterprise', {
                  enterpriseName: org?.enterprise?.displayName,
                })}
          </p>
        )}
      </div>

      <div className={styles.memberListItemOptions}>
        <WorkspaceBoards idMember={idMember} idOrganization={idOrganization} />
        {member?.activityBlocked && (
          <FormattedMessage
            id="templates.member_list_item_detail.deactivated"
            description="User is deactivated"
            defaultMessage="Deactivated"
          />
        )}
        {isCurrentMemberWorkspaceAdmin && (
          <>
            <AddToWorkspaceButton
              memberId={idMember}
              workspaceId={idOrganization}
            />
            <RemoveMemberFromWorkspaceButton
              idMember={idMember}
              idOrganization={idOrganization}
            />
          </>
        )}
      </div>
    </div>
  );
}
